import { useState } from "react";
import { CheckCircle, Send, Edit2 } from "lucide-react";
import { supabase } from "../lib/supabase";

interface WhatsAppSectionProps {
  userId: string;
  initialPhone: string | null;
  initialVerifiedAt: string | null;
}

type Status = "unlinked" | "pending" | "linked";

export function WhatsAppSection({
  userId,
  initialPhone,
  initialVerifiedAt,
}: WhatsAppSectionProps) {
  const initialStatus: Status = initialVerifiedAt
    ? "linked"
    : initialPhone
    ? "pending"
    : "unlinked";

  const [status, setStatus] = useState<Status>(initialStatus);
  const [phone, setPhone] = useState(initialPhone ?? "");
  const [savedPhone, setSavedPhone] = useState(initialPhone);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const sendInvite = async (overridePhone?: string) => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-invite-verification`;
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error("Niet ingelogd, ververs de pagina en probeer opnieuw");
      }
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ phone: overridePhone ?? phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Verzenden mislukt");
      setSavedPhone(data.normalized_phone);
      setStatus("pending");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const unlink = async () => {
    setLoading(true);
    try {
      await supabase
        .from("users")
        .update({ whatsapp_phone: null, whatsapp_verified_at: null })
        .eq("id", userId);
      setStatus("unlinked");
      setPhone("");
      setSavedPhone(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-[#231F20] mb-2 flex items-center gap-2">
        WhatsApp {status === "linked" && <CheckCircle className="w-5 h-5 text-green-600" />}
      </h2>

      {status === "unlinked" && (
        <>
          <p className="text-gray-600 mb-4">
            Stuur een voice note of bericht naar dibbi en wij maken er een blog-idee van.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mijn WhatsApp-nummer
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+31612345678"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3"
          />
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <button
            onClick={() => sendInvite()}
            disabled={loading || phone.length < 8}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Opslaan en koppelen
          </button>
        </>
      )}

      {status === "pending" && (
        <>
          <p className="text-gray-700 mb-3">
            We hebben je een bericht gestuurd op <strong>{savedPhone}</strong>.
            Tik daarin op <strong>Ja, koppelen</strong> (of antwoord met JA) om de koppeling te bevestigen.
          </p>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex gap-3 text-sm">
            <button
              onClick={() => sendInvite(savedPhone ?? phone)}
              disabled={loading}
              className="text-[#FF6B35] hover:underline"
            >
              Opnieuw sturen
            </button>
            <button
              onClick={() => setStatus("unlinked")}
              className="text-gray-600 hover:underline inline-flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" /> Wijzigen
            </button>
          </div>
        </>
      )}

      {status === "linked" && (
        <>
          <p className="text-gray-700 mb-2">
            Gekoppeld aan <strong>{savedPhone}</strong>.
          </p>
          <p className="text-gray-600 mb-4">
            Stuur je voice notes en ideeën gewoon in je WhatsApp-gesprek met dibbi.
          </p>
          <button
            onClick={unlink}
            disabled={loading}
            className="text-sm text-red-600 hover:underline"
          >
            Koppeling verbreken
          </button>
        </>
      )}
    </div>
  );
}
